import { useCallback, useEffect, useState } from 'react';
import { useIoContext, useOverlayContext } from '~/contexts';
import { useBiometricMethodLabel } from '~/hooks/useBiometricMethodLabel';
import { useLocaleKeys } from '~/i18n';
export const useBiometricLoginToggle = (toastTestIds)=>{
    const { native, cookies } = useIoContext();
    const { showSuccessToast, showErrorToast } = useOverlayContext();
    const { t: profileTexts } = useLocaleKeys('pages.profile');
    const method = useBiometricMethodLabel();
    const enabled = native.isNative;
    const [available, setAvailable] = useState(false);
    const [setupNeeded, setSetupNeeded] = useState(false);
    const [checked, setChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    useEffect(()=>{
        if (!enabled) {
            return undefined;
        }
        let cancelled = false;
        const evaluate = async ()=>{
            const [{ isAvailable, isStrong, deviceIsSecure, setupNeeded: isSetupNeeded }, enrolled] = await Promise.all([
                native.biometric.getBiometryStatus(),
                native.biometric.isEnrolled()
            ]);
            if (cancelled) {
                return;
            }
            setAvailable(isAvailable && isStrong && deviceIsSecure);
            setSetupNeeded(isSetupNeeded);
            setChecked(enrolled);
        };
        void evaluate();
        let unsubscribe;
        void native.biometric.onBiometryChange(()=>void evaluate()).then((fn)=>{
            if (cancelled) {
                fn();
            } else {
                unsubscribe = fn;
            }
        });
        return ()=>{
            cancelled = true;
            unsubscribe === null || unsubscribe === void 0 ? void 0 : unsubscribe();
        };
    }, [
        enabled
    ]);
    const onToggle = useCallback(async (next)=>{
        setChecked(next);
        setLoading(true);
        try {
            if (next) {
                const token = cookies.getAuthToken();
                const claims = cookies.getClaims();
                const isFullAuth = claims != null && !claims.setPasswordOnly && !claims.setIdentityOnly;
                if (token == null || (claims === null || claims === void 0 ? void 0 : claims.email) == null || !isFullAuth) {
                    throw new Error('biometric enroll requires a fully authenticated session');
                }
                const result = await native.biometric.enroll({
                    token,
                    email: claims.email
                });
                if (result === 'enrolled') {
                    showSuccessToast(profileTexts.biometricSecurity.enabledToast({
                        method
                    }), toastTestIds === null || toastTestIds === void 0 ? void 0 : toastTestIds.success);
                } else {
                    setChecked(false);
                    if (result === 'failed') {
                        showErrorToast(profileTexts.biometricSecurity.errorToast(), toastTestIds === null || toastTestIds === void 0 ? void 0 : toastTestIds.error);
                    }
                }
            } else {
                await native.biometric.disable();
            }
        } catch (unused) {
            setChecked(!next);
            showErrorToast(profileTexts.biometricSecurity.errorToast(), toastTestIds === null || toastTestIds === void 0 ? void 0 : toastTestIds.error);
        } finally{
            setLoading(false);
        }
    }, // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    [
        method
    ]);
    return {
        available,
        setupNeeded,
        checked,
        loading,
        method,
        onToggle
    };
};
